import { SelectTriggerSkeleton } from '@/components/core';
import { DatePicker } from '@/components/core/date-picker/date-picker';
import { Button } from '@/components/ui-kit/button';
import {
  Dialog,
  DialogContent,
  DialogCancelButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui-kit/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui-kit/radio-group';
import { Textarea } from '@/components/ui-kit/textarea';
import { dateOnlyToUtcIso } from '@/lib/utils/custom-date/custom-date';
import { translatedZodResolver } from '@/lib/utils/validation/translated-zod-resolver';
import { DepartmentFilter } from '@/modules/org-setup/hr-department-manager';
import {
  EmployeeOption,
  EmployeeSearchPicker,
  useDepartmentScope,
} from '@/modules/shared/hr-lookups';
import { useAuthStore } from '@/state/store/auth';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useRequestToilForEmployee } from '../../hooks/use-toil-manager';
import {
  toilForEmployeeFormSchema,
  ToilForEmployeeFormValues,
} from '../../schemas/toil-for-employee-form-schema';

export interface ToilForEmployeeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY_VALUES: ToilForEmployeeFormValues = {
  UserId: '',
  ToilType: 'FullDay',
  ToilDate: '',
  Reason: '',
};

/**
 * Files TOIL for an employee, past days included (hrm::toil::request-for-others). The employee search
 * is limited like the TOIL lists: everyone for a user who sees every department, otherwise one of the
 * user's departments (picked first when there are several). TOIL_REQUEST checks the permission, the
 * department and the TOIL rules again.
 */
export const ToilForEmployeeDialog = ({ open, onOpenChange }: ToilForEmployeeDialogProps) => {
  const { t } = useTranslation();
  const myUserId = useAuthStore((state) => state.user?.itemId ?? '');
  const { canViewAll, departments, isLoading: isScopeLoading } = useDepartmentScope();
  const [departmentId, setDepartmentId] = useState('');
  const [employee, setEmployee] = useState<EmployeeOption | null>(null);
  const { mutateAsync: requestToil, isPending } = useRequestToilForEmployee();

  const form = useForm<ToilForEmployeeFormValues>({
    resolver: translatedZodResolver(toilForEmployeeFormSchema, t),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (!open) return;
    form.reset(EMPTY_VALUES);
    setEmployee(null);
    setDepartmentId('');
  }, [open, form]);

  // A scoped user searches one of their departments: the only one, or the one they pick.
  const searchDepartmentId =
    departmentId || (!canViewAll && departments.length === 1 ? departments[0].ItemId : '');
  const canSearch = canViewAll || Boolean(searchDepartmentId);

  const handleSubmit = async (values: ToilForEmployeeFormValues) => {
    try {
      await requestToil({
        userId: values.UserId,
        // The picked day as a date-only value (`YYYY-MM-DDT00:00:00.000Z`); stored as `ToilDate`.
        toilDate: dateOnlyToUtcIso(values.ToilDate),
        isHalfDay: values.ToilType === 'HalfDay',
        reason: values.Reason.trim(),
      });
      onOpenChange(false);
    } catch {
      // The refusal is toasted by the mutation's global error handler; the dialog stays open.
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('ADD_TOIL_FOR_EMPLOYEE')}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-5">
            <FormField
              control={form.control}
              name="UserId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('EMPLOYEE')}</FormLabel>
                  <div className="space-y-2">
                    <DepartmentFilter
                      value={departmentId}
                      onChange={(next) => {
                        // The picked employee may be outside the new department.
                        setDepartmentId(next);
                        setEmployee(null);
                        field.onChange('');
                      }}
                      selectClassName="w-full"
                    />
                    {/* Not "select a department" while the user's departments are still loading. */}
                    {isScopeLoading ? (
                      <SelectTriggerSkeleton />
                    ) : canSearch ? (
                      <FormControl>
                        <EmployeeSearchPicker
                          value={field.value}
                          selectedLabel={employee?.DisplayName}
                          departmentId={searchDepartmentId || undefined}
                          excludeIds={myUserId ? [myUserId] : []}
                          onSelect={(picked) => {
                            setEmployee(picked);
                            field.onChange(picked.UserId);
                          }}
                        />
                      </FormControl>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('SELECT_DEPARTMENT')}</p>
                    )}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ToilType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('DURATION')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                      className="flex gap-4"
                    >
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <RadioGroupItem value="FullDay" className="cursor-pointer" />
                        {t('FULL_DAY')}
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 text-sm">
                        <RadioGroupItem value="HalfDay" className="cursor-pointer" />
                        {t('HALF_DAY')}
                      </label>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="ToilDate"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('DATE')}</FormLabel>
                  <FormControl>
                    {/* Past days allowed: TOIL worked earlier can be filed afterwards. */}
                    <DatePicker
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="Reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('REASON')}</FormLabel>
                  <FormControl>
                    <Textarea rows={3} maxLength={100} placeholder={t('REASON')} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 sm:gap-0">
              <DialogCancelButton onClick={() => onOpenChange(false)}>
                {t('CANCEL')}
              </DialogCancelButton>
              <Button type="submit" loading={isPending} disabled={isPending}>
                {t('SUBMIT')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
